import {
	useEffect,
	useContext,
	useRef,
	MutableRefObject,
	useMemo,
} from 'react';
import { useParams } from '@tanstack/react-router';

import { IssueContext } from '../Issue.context';

export function useLoad(issue: any, setIndex: (i: number) => void) {
	const params = useParams({ strict: false });
	const { articleId } = params;
	useEffect(() => {
		if (!issue || !articleId) {
			return;
		}

		for (let i = 0; i < issue.articles.length; i++) {
			const article = issue.articles[i];
			if (article.id === articleId || article.slug === articleId) {
				setIndex(i);
			}
		}
	}, [issue, params]);
}

/**
 * React hook that handles internal links in the article
 *
 */
export function useInternalLink(): MutableRefObject<HTMLDivElement | null> {
	const contentRef = useRef<HTMLDivElement | null>(null);
	const { issue, currentArticle, setCurrentArticle } = useContext(
		IssueContext,
	);

	const articles: Map<string, any> | undefined = useMemo(() => {
		if (!issue) {
			return undefined;
		}

		return issue.articles.reduce((acc: Map<string, any>, article: any) => {
			const { ArticleID } = article;
			acc.set(`${ArticleID}`, article);
			return acc;
		}, new Map<string, any>());
	}, [issue]);

	useEffect(() => {
		if (!contentRef || !issue || !articles || !currentArticle) {
			return;
		}

		if (!contentRef.current) {
			return;
		}

		setTimeout(() => {
			processNodes({
				article: currentArticle,
				articles,
				issue,
				onSelectArticle: (targetArticle, hash) => {
					targetArticle.hash = hash;
					if (targetArticle.id !== currentArticle.id) {
						setCurrentArticle(targetArticle);
					}
				},
			});
		}, 100);
	}, [contentRef, issue, articles, currentArticle]);

	return contentRef;
}

function processNodes(args: ProcessArticleNodeArgs) {
	const { article, articles, issue, onSelectArticle } = args;
	const element = document.getElementById(`article-${article.id}`);
	if (!element) {
		return;
	}
	Array.from(element.querySelectorAll('a'))
		.filter(filterLinkFormat)
		.forEach(
			putInternalLink({
				articles,
				issue,
				article,
				onSelectArticle,
			}),
		);
}

interface ProcessArticleNodeArgs {
	article: any;
	articles: Map<string, any>;
	issue: any;
	onSelectArticle: OnSelectArticleFn;
}

type OnSelectArticleFn = (article: any, hash: string | undefined) => void;

function filterLinkFormat(element: HTMLAnchorElement) {
	if (element.getAttribute('data-article-id')) {
		return true;
	}
	const href = element.href
		.replace('https://', '')
		.replace('http://', '')
		.replace(element.host, '')
		.replace(/#[A-Z0-9a-z-]*/gim, '');
	return /\/article\/[0-9]/gim.test(href);
}

function putInternalLink(args: PutInternalLinkArgs): PutInternalLink {
	const { articles, issue, article, onSelectArticle } = args;
	return (el: HTMLAnchorElement) => {
		if (el.getAttribute('data-article-id')) {
			return;
		}

		const [, , targetArticleId] = el.href
			.replace('https://', '')
			.replace('http://', '')
			.replace(el.host, '')
			.replace(/#[A-Z0-9a-z-]*/gim, '')
			.split('/');
		const targetArticle = articles.get(targetArticleId);
		if (!targetArticle) {
			return;
		}
		let href = [
			'/issues',
			issue.slug || issue.id,
			targetArticle.slug || targetArticle.id,
		].join('/');

		let elementId: string | undefined = undefined;

		if (el.href.split('#').length > 1) {
			elementId = el.href.split('#')[1];
			href += `#${elementId}`;
		}

		el.setAttribute('href', href);
		// Origin data
		el.setAttribute('data-origin-id', article.id);
		el.setAttribute('data-origin-article-id', article.ArticleID);
		// Target data
		el.setAttribute('data-target-id', targetArticle.id);
		el.setAttribute('data-target-article-id', targetArticle.ArticleID);
		if (elementId) {
			el.setAttribute('data-target-element-id', elementId);
		}

		const isSameArticle = targetArticleId === article.ArticleID;

		el.setAttribute('data-is-same-article-id', `${isSameArticle}`);
		el.onclick = e => {
			e.preventDefault();
			onSelectArticle(targetArticle, elementId);
			// TODO: Scroll to element
		};
	};
}

interface PutInternalLinkArgs {
	article: any;
	articles: Map<string, any>;
	issue: any;
	onSelectArticle: OnSelectArticleFn;
}

type PutInternalLink = (element: HTMLAnchorElement) => void;
